'use client'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Form,
  Button,
  Space,
  Row,
  Col,
  notification,
  Radio,
  Select,
  InputNumber,
  Input
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import { useTranslation } from 'react-i18next'
import ActivityGuideDrawer from '../components/ActivityGuideDrawer'
import CompaniesComboBox from '../components/Combobox/CompaniesComboBox'
import { useLazyQuery, useMutation } from '@apollo/client'
import { WorklogCreateInput } from '../../../generated/graphql'
import { CREATE_WORKLOG } from '../gql/verteOfficeGQL'
import { getPayload } from '../../../utils/auth'
import DomainCombobox from '../components/Combobox/DomainsGeneralComboBox'
import { normalizeKey } from '../../shared/verteoffice'
import { COMPANIES } from '../components/Combobox/ComboboxGQL'
import { GET_DOMAINS } from '../gql/registroHorasGQLignore'
import { DOMAIN_CONFIG } from '../shared/domainConfig'
import { buildDomainLists } from '../shared/buildDomainLists'
import { buildActivityMaxHoursLookup } from '../shared/buildActivityMaxHoursLookup'
import { buildDependencyConfiguration } from '../shared/dependencyConfig'
import { buildAllObjectivesOptions, ObjectiveOption } from '../shared/objectivesOptions'
import {
  BUSINESS_DATE_FORMAT,
  businessDateToUtcStartOfDay,
  getTodayBusinessDate,
  pickerMomentToBusinessDate,
} from '../shared/businessDate'
import { canonicalizeWorklogListFields } from '../shared/canonicalizeWorklogListFields'
import { getMaxHoursForWorklogActivity } from '../shared/worklogHourLimits'
import { withWorklogCreationOrigin, WORKLOG_ORIGINS } from '../shared/worklogOrigin'
import {
  applyWorklogForcedDefaults,
  buildWorklogForcedDefaultsLookup,
  resolveWorklogForcedDefaults,
  WORKLOG_FORCED_PRODUCT,
  WORKLOG_VERTEBRA_COMPANY_ID,
  WORKLOG_VERTEBRA_COMPANY_NAME,
} from '../shared/worklogForcedDefaults'

// Stable document reference — GET_DOMAINS() uses interpolated template literals
// so gql cannot cache it; calling it inside the component would create a new
// document object on every render, resetting Apollo's useLazyQuery hook state.
const DOMAINS_QUERY = GET_DOMAINS()

type createWorklogInputType = { data: WorklogCreateInput }
type CompanyType = 'compania' | 'prorrateo'
type SearchableOption = { value: string; label: string; normalizedLabel: string }
type SearchableObjectiveOption = ObjectiveOption & { normalizedLabel: string }

interface CreateTimeRecordFormProps {
  onCancel: () => void
  onClose: () => void
  onBack?: () => void
}

export const CreateTimeRecordForm: React.FC<CreateTimeRecordFormProps> = ({
  onCancel,
  onClose,
  onBack
}) => {
  const [createWorklog, { loading }] = useMutation<any, createWorklogInputType>(
    CREATE_WORKLOG,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const selectedSpecificActivity = Form.useWatch('specific_activity', form) as string | undefined
  const selectedObjective = Form.useWatch('objective', form) as string | undefined
  const PAYLOAD = getPayload()
  const [companyType, setCompanyType] = useState<CompanyType>('compania')
  const [companyProducts, setCompanyProducts] = useState<string[]>([])
  const [selectedGeneralActivity, setSelectedGeneralActivity] = useState<
    string | undefined
  >()
  const [showReference, setShowReference] = useState(false)
  const hadForcedActivityRef = useRef(false)

  const [loadDomains, { data: domainsData }] = useLazyQuery(DOMAINS_QUERY, {
    fetchPolicy: 'no-cache',
  })

  const domainDataList = useMemo(() => (
    buildDomainLists(domainsData, DOMAIN_CONFIG)
  ), [domainsData])

  const activityMaxHoursByName = useMemo(
    () => buildActivityMaxHoursLookup(domainsData, DOMAIN_CONFIG),
    [domainsData]
  )

  const worklogForcedDefaultsByActivity = useMemo(
    () => buildWorklogForcedDefaultsLookup(domainsData, DOMAIN_CONFIG, domainDataList),
    [domainsData, domainDataList]
  )

  const allObjectivesOptions = useMemo<SearchableObjectiveOption[]>(() => {
    if (!domainsData) return []
    return buildAllObjectivesOptions(domainDataList)
      .map(option => ({
        ...option,
        normalizedLabel: normalizeKey(option.label),
      }))
  }, [domainDataList, domainsData])

  const dependencyConfig = useMemo(
    () => buildDependencyConfiguration(domainDataList),
    [domainDataList]
  )

  const allSpecificActivityOptions = useMemo(
    () => Array.from(
      new Set(
        Object.entries(dependencyConfig.general_activity?.options ?? {})
          .filter(([generalActivity]) => generalActivity !== 'default')
          .flatMap(([, options]) => options.specific_activity ?? [])
      )
    ).map((value) => ({ value, label: value, normalizedLabel: normalizeKey(value) })),
    [dependencyConfig]
  )

  const filteredSpecificActivityOptions = useMemo(() => {
    if (!selectedGeneralActivity) return allSpecificActivityOptions

    const normalizedGeneralActivity = normalizeKey(selectedGeneralActivity)
    const matchedEntry = Object.entries(dependencyConfig.general_activity?.options ?? {})
      .find(([generalActivity]) =>
        generalActivity !== 'default' && normalizeKey(generalActivity) === normalizedGeneralActivity
      )

    const options = matchedEntry?.[1]?.specific_activity ?? []
    return options.map((value) => ({ value, label: value, normalizedLabel: normalizeKey(value) }))
  }, [allSpecificActivityOptions, dependencyConfig, selectedGeneralActivity])

  const filteredObjectiveOptions = useMemo(() => {
    if (selectedSpecificActivity) {
      return allObjectivesOptions.filter((option) =>
        normalizeKey(option.specificActivity) === normalizeKey(selectedSpecificActivity)
      )
    }

    if (selectedGeneralActivity) {
      return allObjectivesOptions.filter((option) =>
        normalizeKey(option.generalActivity) === normalizeKey(selectedGeneralActivity)
      )
    }

    return allObjectivesOptions
  }, [allObjectivesOptions, selectedGeneralActivity, selectedSpecificActivity])

  const filterSearchableOption = useCallback((input: string, option?: SearchableOption) =>
    String(option?.normalizedLabel ?? '').includes(normalizeKey(input)), [])

  const objectivesLoading = !domainsData
  const maxHoursForSelectedActivity = getMaxHoursForWorklogActivity(
    activityMaxHoursByName,
    selectedObjective,
    selectedSpecificActivity
  )

  const forcedActivityDefaults = useMemo(() => {
    const forced = resolveWorklogForcedDefaults({
      general_activity: selectedGeneralActivity,
      specific_activity: selectedSpecificActivity,
      activity_objective: selectedObjective,
    }, worklogForcedDefaultsByActivity)
    return forced?.reason === 'activity' ? forced : null
  }, [
    selectedGeneralActivity,
    selectedSpecificActivity,
    selectedObjective,
    worklogForcedDefaultsByActivity,
  ])

  const productOptions = forcedActivityDefaults || companyType === 'prorrateo'
    ? [WORKLOG_FORCED_PRODUCT]
    : companyProducts

  useEffect(() => {
    if (forcedActivityDefaults) {
      hadForcedActivityRef.current = true
      setCompanyType('compania')
      setCompanyProducts([WORKLOG_FORCED_PRODUCT])
      form.setFieldsValue({
        company: {
          label: forcedActivityDefaults.company_name,
          value: forcedActivityDefaults.company_id,
        },
        product: forcedActivityDefaults.product,
      })
      return
    }

    if (hadForcedActivityRef.current) {
      hadForcedActivityRef.current = false
      setCompanyProducts([])
      form.setFieldsValue({ company: undefined, product: undefined })
    }
  }, [forcedActivityDefaults, form])

  const [getDefaultCompany] = useLazyQuery(COMPANIES, {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const company = data?.companies?.[0]
        if (company?.product) {
          setCompanyProducts(company.product)
          if (company.product.length === 1) {
            form.setFieldsValue({ product: company.product[0] })
          }
        }
      }
    })

    useEffect(() => {
      loadDomains()
    if (PAYLOAD?.company) {
        getDefaultCompany({
          variables: {
            where: { id: { equals: PAYLOAD.company }, deleted_at: { equals: null }, is_active: { equals: true } },
            first: 1,
            last: 0
          }
        })
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  const handleCompanyFull = (item: {
    id: string
    name: string
    product: string[]
  }) => {
    if (forcedActivityDefaults) return
    const products = item.product ?? []
    setCompanyProducts(products)
    form.setFieldsValue({ product: products.length === 1 ? products[0] : undefined })
  }

  const handleCompanyTypeChange = (e: any) => {
    if (forcedActivityDefaults) return
    const newType = e.target.value
    setCompanyType(newType)
    if (newType === 'prorrateo') {
      setCompanyProducts([WORKLOG_FORCED_PRODUCT])
      form.setFieldsValue({ company: undefined, product: WORKLOG_FORCED_PRODUCT })
    } else {
      setCompanyProducts([])
      form.setFieldsValue({ company: undefined, product: undefined })
    }
  }

  const handleGeneralActivityChange = (value: string) => {
    setSelectedGeneralActivity(value)
    form.setFieldsValue({ specific_activity: undefined, objective: undefined })
  }

  const handleGeneralActivityClear = () => {
    setSelectedGeneralActivity(undefined)
    form.setFieldsValue({ specific_activity: undefined, objective: undefined })
  }

  const handleSpecificActivityChange = (_value: string) => {
    form.setFieldsValue({ objective: undefined })
    void form.validateFields(['total_hours']).catch(() => undefined)
  }

  const handleObjectiveChange = (_value: string, option: ObjectiveOption | ObjectiveOption[]) => {
    const opt = Array.isArray(option) ? option[0] : option
    if (!opt) {
      return
    }
    setSelectedGeneralActivity(opt.generalActivity)
    form.setFieldsValue({
      activity: opt.generalActivity,
      specific_activity: opt.specificActivity
    })
    void form.validateFields(['total_hours']).catch(() => undefined)
  }

  const handleSubmit = async (values: any) => {
    const isProrrateo = companyType === 'prorrateo'
    const businessDate = pickerMomentToBusinessDate(values.start_time)
    const startTimeIso = businessDateToUtcStartOfDay(businessDate)
    if (!startTimeIso) {
      notification.error({
        message: t('error_creating_worklog'),
        description: t('validation_invalid_date')
      })
      return
    }
    try {
      const canonicalizedData = canonicalizeWorklogListFields({
        general_activity: values.activity,
        activity_objective: values.objective,
        company_name: forcedActivityDefaults
          ? WORKLOG_VERTEBRA_COMPANY_NAME
          : isProrrateo ? 'PRORRATEO' : values.company.label,
        company_id: forcedActivityDefaults
          ? WORKLOG_VERTEBRA_COMPANY_ID
          : isProrrateo ? 'PRORRATEO' : values.company.value,
        specific_activity: values.specific_activity,
        status: 'COMPLETE',
        start_time: startTimeIso,
        end_time: startTimeIso,
        total_hours: values.total_hours,
        observation: values.observation,
        user_name: `${PAYLOAD?.names} ${PAYLOAD?.lastnames}`,
        user_id: PAYLOAD?.id,
        user_updated: PAYLOAD?.nickname,
        user_created: PAYLOAD?.nickname,
        product: (isProrrateo || forcedActivityDefaults) ? WORKLOG_FORCED_PRODUCT : values.product
      }, domainDataList)
      const payloadData = applyWorklogForcedDefaults(
        canonicalizedData,
        worklogForcedDefaultsByActivity
      )

      await createWorklog({
        variables: {
          data: withWorklogCreationOrigin(
            payloadData,
            WORKLOG_ORIGINS.POPUP_FINALIZED_FORM
          ) as WorklogCreateInput
        }
      })

      notification.success({ message: t('worklog_created_successfully') })
      onClose()
    } catch (error: any) {
      notification.error({
        message: t('error_creating_worklog'),
        description: error.message
      })
    } finally {
      form.resetFields()
      setCompanyType('compania')
      setCompanyProducts([])
      setSelectedGeneralActivity(undefined)
    }
  }

  return (
    <>
    <Form
      form={form}
      layout='vertical'
      onFinish={handleSubmit}
      initialValues={{
        company: { label: PAYLOAD?.company_name, value: PAYLOAD?.company }
      }}
    >
      <Row gutter={[16, 8]}>
        <Col span={24} style={{ textAlign: 'right', marginBottom: 4 }}>
          <Button
            type='link'
            size='small'
            icon={<QuestionCircleOutlined />}
            onClick={() => setShowReference(true)}
          >
            Ver guía de actividades
          </Button>
        </Col>
        <Col span={24}>
          <Form.Item
            name='activity'
            label={t('hh_col_general_activity')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <DomainCombobox
              showSearch
              filterOption={false}
              size='small'
              allowClear
              style={{ width: '100%' }}
              placeholder={t('hh_col_general_activity')}
              type='GENERAL_ACTIVITY'
              onChange={handleGeneralActivityChange}
              onClear={handleGeneralActivityClear}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='specific_activity'
            label={t('hh_col_activity_objective')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Select
              showSearch
              filterOption={filterSearchableOption}
              size='small'
              allowClear
              style={{ width: '100%' }}
              placeholder={t('hh_col_activity_objective')}
              onChange={handleSpecificActivityChange}
              options={filteredSpecificActivityOptions}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label={t('company_type')} style={{ marginBottom: 8 }}>
            <Radio.Group
              value={companyType}
              onChange={handleCompanyTypeChange}
              buttonStyle='solid'
              size='small'
              style={{ width: '100%' }}
            >
              <Radio.Button
                value='compania'
                style={{ width: '50%', textAlign: 'center' }}
              >
                {t('company')}
              </Radio.Button>
              <Radio.Button
                value='prorrateo'
                disabled={Boolean(forcedActivityDefaults)}
                style={{ width: '50%', textAlign: 'center' }}
              >
                PRORRATEO
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>

        {companyType === 'compania' && (
          <Col span={12}>
            <Form.Item
              name='company'
              label={t('company')}
              rules={[{ required: true, message: t('field_required') }]}
              style={{ marginBottom: 8 }}
            >
              <CompaniesComboBox
                showSearch
                filterOption={false}
                labelInValue
                size='small'
                allowClear
                style={{ width: '100%' }}
                placeholder={t('company')}
                onSelectFull={handleCompanyFull}
                disabled={Boolean(forcedActivityDefaults)}
              />
            </Form.Item>
          </Col>
        )}

        {companyType === 'compania' && (
          <Col span={12}>
            <Form.Item
              name='product'
              label={t('product')}
              rules={[{ required: true, message: t('field_required') }]}
              style={{ marginBottom: 8 }}
            >
              <Select
                size='small'
                allowClear
                placeholder={t('product')}
                disabled={Boolean(forcedActivityDefaults) || productOptions.length === 0}
                style={{ width: '100%' }}
              >
                {productOptions.map(p => (
                  <Select.Option key={p} value={p}>
                    {p}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <Col span={24}>
          <Form.Item
            name='objective'
            label={t('hh_col_specific_activity')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Select
              showSearch
              allowClear
              size='small'
              style={{ width: '100%' }}
              placeholder={t('hh_col_specific_activity')}
              loading={objectivesLoading}
              filterOption={filterSearchableOption}
              onChange={handleObjectiveChange}
              options={filteredObjectiveOptions}
            />
          </Form.Item>
        </Col>

        {/* Campos exclusivos de actividad finalizada */}
        <Col span={12}>
          <Form.Item
            name='start_time'
            label={t('dateFirst')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <DatePicker
              size='small'
              style={{ width: '100%' }}
              format={BUSINESS_DATE_FORMAT}
              inputReadOnly
              disabledDate={current => {
                if (!current) return false
                return current.format(BUSINESS_DATE_FORMAT) > getTodayBusinessDate()
              }}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='total_hours'
            label={t('hours')}
            rules={[
              { required: true, message: t('field_required') },
              {
                type: 'number',
                min: 0.1,
                validator: async (_, value) => {
                  if (value == null || value === '') return
                  if (typeof value !== 'number') return
                  if (value - maxHoursForSelectedActivity <= 0.000001) return
                  throw new Error(t('hh_validation_activity_max_hours_exceeded', {
                    activity: selectedObjective || selectedSpecificActivity || '',
                    max: maxHoursForSelectedActivity,
                  }))
                }
              }
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber
              size='small'
              min={0.1}
              max={maxHoursForSelectedActivity}
              step={0.01}
              style={{ width: '100%' }}
              placeholder={t('hours')}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name='observation'
            label={t('observations')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Input.TextArea
              size='small'
              rows={3}
              style={{ width: '100%' }}
              placeholder={t('observations')}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 0
            }}
          >
            <Space>
              {onBack && (
                <Button onClick={onBack} size='small' shape='round'>
                  {t('back')}
                </Button>
              )}
              <Button onClick={onCancel} size='small' shape='round' danger>
                {t('cancel')}
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                shape='round'
                size='small'
                loading={loading}
              >
                {t('save')}
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
    <ActivityGuideDrawer open={showReference} onClose={() => setShowReference(false)} />
    </>
  )
}

export default CreateTimeRecordForm
